/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  allInterpreters,
  allParsingInstructions,
  createFactory,
  DocumentCondition,
  Filter,
  JsInterpreter,
  ParsingInstruction,
} from "@ucast/mongo2js";
import { FieldValues } from "react-hook-form";

import { PermissionMap } from "../dto";
import { RydeUiUnit, TPermissionAction } from "../models";
import { hasKeys } from "./objects.helpers";

export type Permission = { entity: RydeUiUnit; action: TPermissionAction };

export interface IFieldValuesWithPermissions extends FieldValues {
  permissions?: PermissionMap;
}

// -----------------------------
// Custom Operator Enum
// -----------------------------
export const CustomOperators = {
  PERMISSION_HAS: "$hasPermission", // Checks if user has at least one required permission
  PERMISSION_HAS_NOT: "$hasNotPermission", // Checks if user lacks all specified permissions
} as const;

type CustomOperatorKey = (typeof CustomOperators)[keyof typeof CustomOperators];

// -----------------------------
// Operator Entry Type
// -----------------------------
interface ICustomOperatorEntry {
  parser: ParsingInstruction<any>;
  interpreter: JsInterpreter<any, any>; // Use any for context to allow both default and custom
}

// -----------------------------
// Permissions Operator Handlers
// -----------------------------
const permissionOperators: Record<CustomOperatorKey, ICustomOperatorEntry> = {
  [CustomOperators.PERMISSION_HAS]: {
    parser: {
      type: "document",
      validate(_, value) {
        if (!Array.isArray(value)) {
          throw new Error(`${CustomOperators.PERMISSION_HAS} must be an array`);
        }
      },
      parse(_, value) {
        return new DocumentCondition(CustomOperators.PERMISSION_HAS, value);
      },
    },
    interpreter: (condition, context: IFieldValuesWithPermissions) => {
      const required = condition.value;
      const actual: Partial<PermissionMap> = context.permissions ?? {};
      return (
        hasKeys(actual) &&
        required.some(
          (r: Permission) =>
            Array.isArray(actual[r.entity]) &&
            actual[r.entity]?.includes(r.action),
        )
      );
    },
  },
  [CustomOperators.PERMISSION_HAS_NOT]: {
    parser: {
      type: "document",
      validate(_, value) {
        if (!Array.isArray(value)) {
          throw new Error(
            `${CustomOperators.PERMISSION_HAS_NOT} must be an array`,
          );
        }
      },
      parse(_, value) {
        return new DocumentCondition(CustomOperators.PERMISSION_HAS_NOT, value);
      },
    },
    interpreter: (condition, context: IFieldValuesWithPermissions) => {
      const required = condition.value;
      const actual: Partial<PermissionMap> = context.permissions ?? {};
      return !required.some(
        (r: Permission) =>
          Array.isArray(actual[r.entity]) &&
          actual[r.entity]?.includes(r.action),
      );
    },
  },
};

// -----------------------------
// Combine default and custom parsers and interpreters
// -----------------------------
const combinedParsers: Record<string, ParsingInstruction<any>> = {
  ...allParsingInstructions,
  ...Object.fromEntries(
    Object.entries(permissionOperators).map(([key, entry]) => [
      key,
      entry.parser,
    ]),
  ),
};

const combinedInterpreters: Record<string, JsInterpreter<any, any>> = {
  ...allInterpreters,
  ...Object.fromEntries(
    Object.entries(permissionOperators).map(([key, entry]) => [
      key,
      entry.interpreter,
    ]),
  ),
};

// -----------------------------
// Factory Creator (no generics, context = any)
// -----------------------------
const createConditionEvaluator = (
  parsers: Record<string, ParsingInstruction<any>>,
  interpreters: Record<string, JsInterpreter<any, any>>,
): Filter => {
  return createFactory(parsers, interpreters);
};

// -----------------------------
// Final guard instance with combined operators
// -----------------------------
export const CustomGuard = createConditionEvaluator(
  combinedParsers,
  combinedInterpreters,
);
